import _ from "lodash";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { graphql, compose } from "lib/apolloClient";
import {
  ButtonDisclosure,
  Dropdown,
  DropdownMenu,
  Modal,
  Button,
  Tooltip,
  SideNavigation,
} from "@toddle-edu/ds-web";
import { StarFilled } from "@toddle-edu/ds-icons";
import { colors } from "@toddle-edu/ds-theme";
import PropTypes from "prop-types";
import { updateSettings } from "modules/ConfigurationThunks";
import withLoader from "UIComponents/GqlWithLoader";
import { useI18n } from "Hooks";
import ACLStore from "lib/aclStore";
import classNames from "classnames";
import {
  sideBarOptions,
  USER_TYPES,
} from "ProgressReportSetup/routes/CommentTemplates/modules/Utils.js";
import { getSettingValue } from "Utils/ConfigurationUtils";
import {
  getUserId,
  getCurrentCurriculumProgramType,
  getSelectedCurriculumId,
} from "modules/CommonSelectors";
import classes from "./AIVoiceContainer.scss";
import {
  createUserVoiceToneSelection,
  deleteUserVoiceToneSelection,
} from "AppComponents/ConfigureAIVoice/modules/ConfigureAiVoiceModule.js";
import { getAIPromptSetsQuery } from "modules/CommonQuery";
import { getAIPromptSetsFromCache } from "modules/CommonGraphqlHelpers";
import { getAIVoiceTonesForReportsFromCache } from "ProgressReportCommon/modules/ProgressReportGraphqlHelpers";
import { getAIVoiceTonesForCurriculumQuery } from "ProgressReportCommon/modules/ProgressReportQuery";
import { getModelOptions } from "AppComponents/GPTPlayground/modules/Utils";
import {
  getOverriddenModelFromLocalStorage,
  setOverriddenModelInLocalStorage,
} from "Utils/AIUtils";
import { shouldAllowOverridingAIModel } from "config";

const styles = {
  modalHeaderContainer: {
    height: "80px",
    padding: "24px 24px 24px 32px",
  },
  modalBodyContainer: {
    padding: "0px",
    overflowY: "auto",
  },
  modalFooterContainer: {
    height: "88px",
    padding: "20px 24px",
  },
  starContainer: {
    color: colors.decorativeSurfaceThree,
  },
};

const modelOptions = [
  { key: "default", label: "default" },
  ...getModelOptions(),
];

const AIVoiceContainer = props => {
  const {
    closeVoiceModal,
    voiceTones,
    isPromptSetSelectionEnabled,
    promptSets,
    selectedAIPromptSets,
    currentCurriculumProgramType,
    currentCurriculumProgramId,
    promptSetEntityType,
    userId,
    selectedAIVoiceTone,
    createUserVoiceToneSelection,
    deleteUserVoiceToneSelection,
    updateSettings,
    handleWandClick,
    isGenerateCommandDisabled,
    shouldShowOverrideModelOptions,
  } = props;
  const { t } = useI18n(["common", "progressReport"]);
  const [currentVoice, setCurrentVoice] = useState(selectedAIVoiceTone);

  const promptSetsOptions = _.map(promptSets, ({ id, label }) => ({
    key: id,
    label,
  }));

  const promptSetField = currentCurriculumProgramType
    ? `${promptSetEntityType}_${currentCurriculumProgramType}`
    : promptSetEntityType;

  const [selectedPromptSet, setSelectedPromptSet] = useState(
    _.get(selectedAIPromptSets, promptSetField, _.first(promptSetsOptions)?.key)
  );

  const fieldHeaderTextContainer = classNames(
    classes.fieldHeaderText,
    "text-heading-6"
  );
  const titleStarContainer = classNames(
    classes.titleStarContainer,
    "text-label-2"
  );
  const toneSubtitleContainer = classNames(
    classes.toneSubtitleContainer,
    "text-body-2"
  );
  const voiceTitleContainer = classNames(
    classes.voiceTitleContainer,
    "text-label-2"
  );

  const [overriddenModel, setOverriddenModelValue] = useState(
    getOverriddenModelFromLocalStorage(promptSetField)
  );

  useEffect(() => {
    // Save the overridden model to local storage when it changes
    setOverriddenModelInLocalStorage({ overriddenModel, promptSetField });
  }, [overriddenModel]);

  const onClickTone = ({ toneId }) => {
    const currentVoice = _.find(voiceTones, { id: toneId });
    setCurrentVoice(currentVoice);
  };

  const getSelectedPromptSet = () =>
    _.find(promptSetsOptions, {
      key: selectedPromptSet,
    })?.label;

  const renderFieldHeader = headerText => (
    <div className={fieldHeaderTextContainer}>{headerText}</div>
  );

  const onUpdateAIPromptSetSetting = async value => {
    try {
      await updateSettings({
        id: userId,
        type: "user",
        settings: [
          {
            name: "AIPromptSet",
            value: JSON.stringify({
              ...selectedAIPromptSets,
              [promptSetField]: value,
            }),
          },
        ],
        showToastMsg: false,
      });
    } catch (error) {
      console.error(error);
    }
  };

  const onClickPromptSetMenu = ({ key }) => {
    setSelectedPromptSet(key);
    if (
      key &&
      !_.isEqual(
        key,
        _.get(
          selectedAIPromptSets,
          promptSetField,
          _.first(promptSetsOptions)?.key
        )
      )
    ) {
      onUpdateAIPromptSetSetting(key);
    }
  };

  const areSettingsChanged = () => {
    const isToneChanged =
      _.get(currentVoice, "id", "") !== _.get(selectedAIVoiceTone, "id", "");

    return isToneChanged;
  };

  const updateVoiceSettings = async () => {
    const voiceToneId = _.get(currentVoice, "id", "");
    const defaultVoiceId = _.find(voiceTones, { isDefault: true })?.id;
    voiceToneId === defaultVoiceId
      ? await deleteUserVoiceToneSelection({
          userId,
          voiceToneId,
          curriculumProgramId: currentCurriculumProgramId,
          voiceTones,
        })
      : await createUserVoiceToneSelection({
          userId,
          voiceToneId,
          curriculumProgramId: currentCurriculumProgramId,
          voiceTones,
        });
    closeVoiceModal();
  };

  const onAutoGenerateClick = () => {
    updateVoiceSettings();
    closeVoiceModal();
    handleWandClick({ selectedTone: currentVoice });
  };

  const onSideNavigationClick = key => onClickTone({ toneId: key });

  const onClickOverrideModelMenu = e => {
    setOverriddenModelValue(e.key);
  };

  return (
    <>
      <Modal.Header
        isClosable
        hasSeparator={true}
        title={t("progressReport:select_voice_output")}
        style={styles.modalHeaderContainer}
      />
      <Modal.Body style={styles.modalBodyContainer}>
        <div className={classes.bodyContainer}>
          <div className={classes.fullWidthContainer}>
            <div className={classes.navigationToneContainer}>
              <div className={classes.navigationContainer}>
                <SideNavigation
                  options={sideBarOptions({
                    voiceTones,
                    currentVoice,
                    selectedTone: selectedAIVoiceTone,
                    t,
                    isSelectionVisible: true,
                  })}
                  theme="subtle"
                  padding={"16px 0px 0px"}
                  selectedOptionKey={_.get(currentVoice, "id", "")}
                  onClick={onSideNavigationClick}
                  dsVersion={"2.0"}
                />
              </div>
              <div className={classes.toneDetailsContainer}>
                <div className={classes.toneDetailsHeaderContainer}>
                  <div className={classes.toneDetailsLeftHeaderContainer}>
                    <span className={titleStarContainer}>
                      {_.get(currentVoice, "title", "")}
                      {_.get(currentVoice, "isDefault", false) ? (
                        <Tooltip
                          tooltip={t("progressReport:default_voice")}
                          placement={"bottom"}
                        >
                          <StarFilled style={styles.starContainer} />
                        </Tooltip>
                      ) : null}
                    </span>
                    {_.get(currentVoice, "type", "") === USER_TYPES.global && (
                      <span className={toneSubtitleContainer}>
                        {`${t("progressReport:created_by_toddle")}`}
                      </span>
                    )}
                  </div>
                </div>
                <div className={classes.toneDetailsBodyContainer}>
                  <span className={voiceTitleContainer}>
                    {t("common:voice")}
                  </span>
                  <span
                    className={`text-body-1 mb-3`}
                    dangerouslySetInnerHTML={{
                      __html: _.replace(
                        _.get(currentVoice, "description", ""),
                        /\n/g,
                        "<br />"
                      ),
                    }}
                  />
                  <span className={voiceTitleContainer}>
                    {t("progressReport:output_structure")}
                  </span>
                  <span
                    className="text-body-1"
                    dangerouslySetInnerHTML={{
                      __html: _.replace(
                        _.get(currentVoice, "outputStructure", ""),
                        /\n/g,
                        "<br />"
                      ),
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
          {isPromptSetSelectionEnabled && !_.isEmpty(promptSets) && (
            <div
              className={classNames(
                classes.fullWidthContainer,
                classes.promptSetContainer
              )}
            >
              {renderFieldHeader(t("common:prompt_set"))}
              <Dropdown
                overlay={
                  <DropdownMenu
                    value={selectedPromptSet}
                    options={promptSetsOptions}
                    onClick={onClickPromptSetMenu}
                  />
                }
              >
                <ButtonDisclosure variant="outlined-subtle" isFullWidth={true}>
                  {getSelectedPromptSet()}
                </ButtonDisclosure>
              </Dropdown>
            </div>
          )}
          {/**NOTE: Following model overriding option is ONLY FOR INTERNAL USAGE. */}
          {shouldShowOverrideModelOptions && (
            <div
              className={classNames(
                classes.fullWidthContainer,
                classes.promptSetContainer
              )}
            >
              {renderFieldHeader("Override Model")}
              <Dropdown
                overlay={
                  <DropdownMenu
                    value={overriddenModel}
                    options={modelOptions}
                    onClick={onClickOverrideModelMenu}
                  />
                }
              >
                <ButtonDisclosure
                  _legacy_variant="outlined-subtle"
                  variant="neutral"
                  type="outlined"
                  isFullWidth={true}
                >
                  {_.find(modelOptions, { key: overriddenModel })?.label ||
                    "Default"}
                </ButtonDisclosure>
              </Dropdown>
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer hasSeparator style={styles.modalFooterContainer}>
        <div className={classes.modalFooterContainer}>
          <Button
            variant="outlined-subtle"
            size="large"
            disabled={!areSettingsChanged()}
            onClick={updateVoiceSettings}
          >
            {t("progressReport:select_voice_and_save")}
          </Button>
          <Button
            variant="progressive"
            size="large"
            disabled={isGenerateCommandDisabled}
            onClick={onAutoGenerateClick}
          >
            {t("progressReport:auto_generate")}
          </Button>
        </div>
      </Modal.Footer>
    </>
  );
};

const mapStateToProps = (state, ownProps) => {
  const { promptSetEntityType, courseId } = ownProps;

  const currentCurriculumProgramId = getSelectedCurriculumId(state);
  const userId = getUserId(state);

  const selectedAIPromptSets = getSettingValue({
    name: "AIPromptSet",
    readFromUserAndOrgLevel: true,
  });

  const shouldShowOverrideModelOptions =
    shouldAllowOverridingAIModel &&
    ACLStore.can("Common:EnableOverrideAIModelOptions");

  return {
    promptSetEntityType,
    userId,
    courseId,
    currentCurriculumProgramType: getCurrentCurriculumProgramType(state),
    currentCurriculumProgramId: getSelectedCurriculumId(state),
    isPromptSetSelectionEnabled: ACLStore.can(
      "Common:EnableAIPromptSetSelection"
    ),
    promptSetFilters: {
      entityType: promptSetEntityType,
      curriculumProgramId: currentCurriculumProgramId,
    },
    selectedAIPromptSets,
    organizationId: state.login.userInfo.org_id,
    isData: true,
    isLoading: false,
    shouldShowOverrideModelOptions,
  };
};

AIVoiceContainer.defaultProps = {
  closeVoiceModal: () => {},
  voiceTones: [],
  isPromptSetSelectionEnabled: false,
  promptSets: [],
  selectedAIPromptSets: [],
  currentCurriculumProgramType: "",
  promptSetEntityType: "",
  shouldShowOverrideModelOptions: false,
};

AIVoiceContainer.propTypes = {
  closeVoiceModal: PropTypes.func,
  voiceTones: PropTypes.array,
  isPromptSetSelectionEnabled: PropTypes.bool,
  promptSets: PropTypes.array,
  selectedAIPromptSets: PropTypes.array,
  currentCurriculumProgramType: PropTypes.string,
  promptSetEntityType: PropTypes.string,
  shouldShowOverrideModelOptions: PropTypes.bool,
};

const mapActionCreators = {
  createUserVoiceToneSelection,
  deleteUserVoiceToneSelection,
  updateSettings,
};

export default compose(
  connect(mapStateToProps, mapActionCreators),
  graphql(getAIPromptSetsQuery, {
    name: "getAIPromptSets",
    skip: ({ isPromptSetSelectionEnabled, promptSetFilters }) =>
      !isPromptSetSelectionEnabled || !promptSetFilters.entityType,
    options: ({ promptSetFilters }) => ({
      fetchPolicy: "cache-and-network",
      variables: { filters: promptSetFilters },
    }),
    props({ getAIPromptSets }) {
      const { variables } = getAIPromptSets;
      const queryData = getAIPromptSetsFromCache(variables);
      const promptSets = _.get(queryData, "promptSets", []);

      return {
        isData: !_.isEmpty(queryData),
        isLoading:
          getAIPromptSets["networkStatus"] == 1 ||
          getAIPromptSets["networkStatus"] == 2,
        promptSets,
      };
    },
  }),
  graphql(getAIVoiceTonesForCurriculumQuery, {
    name: "getAIVoiceTonesForCurriculum",
    options: ({ organizationId, currentCurriculumProgramId }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        organizationId,
        curriculumIds: [currentCurriculumProgramId],
      },
    }),
    props({ getAIVoiceTonesForCurriculum, ownProps: { isData, isLoading } }) {
      const data = getAIVoiceTonesForReportsFromCache(
        getAIVoiceTonesForCurriculum.variables
      );
      const curriculumPrograms = _.get(data, "curriculumPrograms", []);
      const voiceTones = _.get(_.first(curriculumPrograms), "voiceTones", []);

      return {
        voiceTones,
        isData: !_.isEmpty(data) && isData,
        isLoading:
          getAIVoiceTonesForCurriculum["networkStatus"] == 1 ||
          getAIVoiceTonesForCurriculum["networkStatus"] == 2 ||
          isLoading,
      };
    },
  }),
  withLoader
)(AIVoiceContainer);
